// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// ** Components
import FadeInView from '@/components/common/FadeInView';
import UnderlineHoverCta from '@/components/common/UnderlineHoverCta';

// ** Types
import type React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export type TextBlockProps = {
	title?: string;
	bodyLeft?: React.ReactNode;
	bodyRight?: React.ReactNode;
	ctaLabel?: string;
	ctaHref?: string;
	titleColor?: string;
	layout?: 'full-width' | '2-col';
	align?: 'left' | 'center';
	/**
	 * Merged onto the title `Typography` after built-in styles (wins on conflicts).
	 * Use for per-page desktop overrides, e.g. `fontSize` / `lineHeight` or `mb` under `theme.breakpoints.up('md')`.
	 */
	titleSx?: SxProps<Theme>;
};

const TextBlock = ({
	title,
	bodyLeft,
	bodyRight,
	ctaLabel,
	ctaHref,
	titleColor,
	titleSx,
	layout = 'full-width',
	align = 'left'
}: TextBlockProps) => {
	const theme = useTheme();
	const isCentered = align === 'center';
	const isTwoCol = layout === '2-col';
	const TWO_COL_MEDIA = `@media (min-width: ${theme.layout.twoColMinWidthPx}px)`;

	const showCta = Boolean(ctaLabel && ctaHref);
	const hasCopy = Boolean(bodyLeft || bodyRight);
	const bothCols = Boolean(bodyLeft && bodyRight);
	// When only one column is provided, render it as a single full-width block.
	const singleBody = !bothCols ? (bodyLeft ?? bodyRight) : null;

	const titleMarginBottom = hasCopy || showCta ? { xs: theme.spacing(5.5), md: theme.spacing(6) } : 0;

	if (!title && !hasCopy && !showCta) return null;

	const renderCta = (opts?: { forceCenter?: boolean; omitTopMargin?: boolean }): React.ReactNode => {
		if (!showCta || !ctaHref || !ctaLabel) return null;

		const centerCta = opts?.forceCenter || (isCentered && !isTwoCol);

		return (
			<Box
				sx={{
					...(opts?.omitTopMargin ? {} : { mt: { xs: theme.spacing(6), md: theme.spacing(8.5) } }),
					...(centerCta ? { display: 'flex', justifyContent: 'center' } : {})
				}}
			>
				<UnderlineHoverCta
					href={ctaHref}
					label={ctaLabel}
					color={theme.palette.text.secondary ?? theme.palette.text.primary}
					typographyVariant="body-md"
					fontWeight={500}
				/>
			</Box>
		);
	};

	return (
		<FadeInView>
			<Box
				sx={{
					...(!isTwoCol ? { textAlign: isCentered ? 'center' : 'left' } : {})
				}}
			>
				{title && (
					<Typography
						component="h2"
						variant="title-lg"
						sx={[
							{
								color: titleColor ?? theme.palette.primary.main,
								mb: titleMarginBottom,
								...(isTwoCol ? { textAlign: isCentered ? 'center' : 'left' } : {})
							},
							...(titleSx != null ? (Array.isArray(titleSx) ? titleSx : [titleSx]) : [])
						]}
					>
						{title}
					</Typography>
				)}

				{/* ── Full-width body ─────────────────────────────────────── */}
				{!isTwoCol && bodyLeft && (
					<Typography
						variant="body-md"
						component="div"
						sx={{
							color: 'text.secondary',
							...(isCentered ? { textAlign: 'center' } : {})
						}}
					>
						{bodyLeft}
					</Typography>
				)}
				{!isTwoCol && renderCta()}

				{/* ── 2-col: both columns ─────────────────────────────────── */}
				{isTwoCol && bothCols && (
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
							gridTemplateAreas: showCta
								? { xs: '"left" "right" "cta"', md: '"left right" "cta ."' }
								: { xs: '"left" "right"', md: '"left right"' },
							columnGap: { md: '5rem' },
							rowGap: showCta ? { xs: theme.spacing(4), md: theme.spacing(8.5) } : { xs: theme.spacing(4), md: 0 }
						}}
					>
						<Box sx={{ gridArea: 'left', minWidth: 0 }}>
							<Typography variant="body-md" component="div" sx={{ color: 'text.secondary' }}>
								{bodyLeft}
							</Typography>
						</Box>

						<Box sx={{ gridArea: 'right', minWidth: 0 }}>
							<Typography variant="body-md" component="div" sx={{ color: 'text.secondary' }}>
								{bodyRight}
							</Typography>
						</Box>

						{showCta && (
							<Box
								sx={{
									gridArea: 'cta',
									minWidth: 0,
									...(isCentered
										? {
												display: 'flex',
												justifyContent: 'center',
												[TWO_COL_MEDIA]: { justifyContent: 'flex-start' }
											}
										: {})
								}}
							>
								{renderCta({ omitTopMargin: true })}
							</Box>
						)}
					</Box>
				)}

				{/* ── 2-col: single column (only one side provided) ───────── */}
				{isTwoCol && singleBody && (
					<Box sx={{ textAlign: isCentered ? 'center' : 'left' }}>
						<Typography variant="body-md" component="div" sx={{ color: 'text.secondary' }}>
							{singleBody}
						</Typography>
						{renderCta({ forceCenter: isCentered })}
					</Box>
				)}

				{/* ── 2-col: CTA only, no copy ────────────────────────────── */}
				{isTwoCol && !hasCopy && showCta && renderCta({ forceCenter: isCentered })}
			</Box>
		</FadeInView>
	);
};

export default TextBlock;
